import { Button } from "@/components/Button";
import OnboardingVideo from "@/components/OnboardingVideo";
import { HeaderTextBlock } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useGetShopifyIntegration,
  usePostUpdateOnboardingProgress,
} from "@/requests/hooks/collect";
import {
  NAVIGATE_FORWARDS,
  SHOPIFY_CHECKOUT_SETTINGS_URL,
} from "@/utils/const";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import disableShopifyConsent from "@/assets/videos/collect/onboarding/disable-shopify-consent.mp4";

const ShopifyDisableConsent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useGetShopifyIntegration();
  const { mutate, isSuccess, isPending } = usePostUpdateOnboardingProgress();

  useEffect(() => {
    if (isSuccess)
      navigate(getOnboardingUrl("SHOPIFY_CONSENT_DISABLED"), {
        state: {
          direction: NAVIGATE_FORWARDS,
        },
      });
  }, [isSuccess]);

  const handleClickContinue = () => {
    mutate({ screen: "SHOPIFY_CONSENT_DISABLED", flow: "MARKETING" });
  };

  const renderContinue = () => (
    <div className="mt-20 flex justify-end md:mt-30">
      <div className="w-full md:w-[unset]">
        <Button
          variant="primary"
          trackingId={{
            eventName: "onboarding.collect.disable-shopify-consent.complete",
          }}
          onClick={handleClickContinue}
          loading={isPending}>
          {t("emailOnboarding.shopifyDisableConsent.right.continueCta")}
        </Button>
      </div>
    </div>
  );

  const renderDescriptionText = () => (
    <ol className="flex list-decimal flex-col gap-10 ps-20 text-start text-primary-dark">
      <li>
        <Trans i18nKey="emailOnboarding.shopifyDisableConsent.left.header.description.step1">
          zero
          <Button
            as="a"
            variant="link"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="text-secondary"
            href={
              data?.shopifyCheckoutSettingsUrl || SHOPIFY_CHECKOUT_SETTINGS_URL
            }
            testId="checkout-settings-link">
            one
          </Button>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyDisableConsent.left.header.description.step2">
          zero
          <span className="font-bold">one</span>
          two
          <span className="font-bold">three</span>
          four
          <span className="font-bold">five</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyDisableConsent.left.header.description.step3">
          zero
          <span className="font-bold">one</span>
          two
          <span className="font-bold">three</span>
        </Trans>
      </li>
    </ol>
  );

  const renderVideo = () => (
    <OnboardingVideo videoLocation={disableShopifyConsent} />
  );

  const renderLeftLayout = () => (
    <HeaderTextBlock
      align="left"
      featureText={t(
        "emailOnboarding.shopifyDisableConsent.left.header.feature",
      )}
      featureTextColor="primary"
      headingText={t(
        "emailOnboarding.shopifyDisableConsent.left.header.heading",
      )}
      headingTextColor="secondary"
      customHtml={renderDescriptionText()}
      customHtmlPosition="bottom"
    />
  );

  const renderRightLayout = () => (
    <>
      {renderVideo()}
      {renderContinue()}
    </>
  );
  return (
    <SplitLayout
      leftPanel={renderLeftLayout()}
      rightPanel={renderRightLayout()}
      backButtonPath={getOnboardingUrl("PRIVACY_EMAIL_SAVED")}
    />
  );
};

export default ShopifyDisableConsent;
